// Shared param construction for the ducted Choose Systems fetches (AHA-6681
// sub-build 5): the page hook (use-ducted-compatibility-fetch) and the
// Existing Equipment pre-warm (use-ducted-prewarm) both build their thunk
// params here, so the pre-warm's requestBodyHash is byte-identical to the
// page's own first fetch by construction — attach-to-in-flight and
// supersede-on-change then come free from the thunk's in-flight hash
// short-circuit and the slice's latest-wins guards.
import { RootState } from 'app/store';
import { CombinationFilter } from 'components/common/combination-filter-controls';
import {
  availableDuctedFuels,
  boundedCodesFor,
  ductedUniverseCodes,
  narrowCodesByFuel
} from 'components/equipment-page/ducted-facet-merge';
import { SystemCompatibleResultsType } from 'features/structure/structure-slice';
import {
  isTonnageSeedApplied,
  tonnageSeedKeyOf
} from 'features/ui/tonnage-filter-seed';
import {
  resolveContextChassisFilters,
  resolveContextFuelSelections,
  resolveContextOrientationFilters
} from 'features/ui/ui-slice';
import {
  ductedSeedChassisSizes,
  ductedSeedFuels,
  ductedSeedOrientations,
  getChooseSystemsEligibleDuctedSystems
} from 'utils/existing-equipment';
import { selectDuctedPairLoads } from 'utils/ducted-pair-context';
import { DuctedPairContextState } from 'utils/ducted-pair-context-types';
import { DuctedFuelToken } from 'utils/kept-system-type-annotation';
import { resolveDuctedEffectiveCodes } from 'utils/recommendations-strip-logic';
import { resolveSystemTonnageSeed } from 'utils/system-tonnage-seed';
import { resolveStructureTonnageSeed } from 'utils/tonnage-seed-state';
import {
  DuctedCoolingOperatingCostParams,
  ductedCoolingOperatingCostParamsFromProject
} from './ducted-cooling-operating-cost-params';
import { DuctedResultSet } from './ducted-compatibility-slice';
import {
  FetchDuctedCombinationsParams,
  resultSetsFor
} from './ducted-compatibility-thunks';

// A summary record with any never-completed entry (a live check or the
// missing-summary derivation is in flight): its code list is not a bound
// yet — the union bound settles only when both sides settle, so callers
// wait rather than skipping or fetching on transient state.
export const summaryPending = (
  summary: SystemCompatibleResultsType | undefined
): boolean =>
  [summary?.kept, summary?.alternatives].some(
    (entry) => !!entry && entry.status === 'loading' && entry.fetchedAt === null
  );

// Kept = components NOT being replaced — all components, not just placed
// ones (matching runNextFetches / the CCB modal). Determines which result
// sets are in play (resultSetsFor): the kept-constrained fetch is skipped
// entirely when zero components are kept.
export const ductedKeptComponentIds = (system: {
  components: Array<{ uniqueId: string; replacing: boolean }>;
}): string[] =>
  system.components
    .filter((component) => !component.replacing)
    .map((component) => component.uniqueId);

// One (system, resultSet) combinations fetch. `fuelSelections` narrow the
// BOUND first (AHA-6796 Fuel chips, narrowCodesByFuel — an all-miss fuel
// selection collapses to the full bound), then `chipCodes` narrow WITHIN
// that fuel-narrowed bound via resolveDuctedEffectiveCodes (a disjoint
// selection collapses to it — never widens past it); `boundedCodes` always
// ride UNNARROWED as the universeCodes that scope the pnet cache fill
// (sub-build 3 — the cache-fill scope must not shrink under a fuel pick).
export function buildDuctedCombinationsFetchParams(args: {
  systemUniqueId: string;
  resultSet: DuctedResultSet;
  keptComponentUniqueIds: string[];
  boundedCodes: string[];
  chipCodes: string[];
  fuelSelections: DuctedFuelToken[];
  filters: CombinationFilter[];
  inStockOnly: boolean;
  priceMin: number | null;
  priceMax: number | null;
  pageNumber: number;
  pageSize: number;
  coolingOperatingCost: DuctedCoolingOperatingCostParams;
  applySizingBands: boolean;
  tonnageSeed: FetchDuctedCombinationsParams['tonnageSeed'];
  ductedSort: FetchDuctedCombinationsParams['ductedSort'];
}): FetchDuctedCombinationsParams {
  return {
    systemUniqueId: args.systemUniqueId,
    resultSet: args.resultSet,
    keptComponentUniqueIds: args.keptComponentUniqueIds,
    systemTypeCodes: resolveDuctedEffectiveCodes(
      args.chipCodes,
      narrowCodesByFuel(args.boundedCodes, args.fuelSelections)
    ),
    universeCodes: args.boundedCodes,
    filters: args.filters,
    inStockOnly: args.inStockOnly,
    priceMin: args.priceMin,
    priceMax: args.priceMax,
    pageNumber: args.pageNumber,
    pageSize: args.pageSize,
    coolingOperatingCost: args.coolingOperatingCost,
    applySizingBands: args.applySizingBands,
    tonnageSeed: args.tonnageSeed,
    ductedSort: args.ductedSort
  };
}

// The post-reset DEFAULT Choose Systems request set (AHA-6681 sub-build 5
// pre-warm): for every eligible ducted system with settled wants and a
// resolvable non-empty recommended bound, the params of the page's FIRST
// fetch after its once-per-context reset — empty chips (= all recommended)
// and page 1 (exactly what resetDuctedSystemTypeCodes lands), plus the
// values the reset deliberately carries (spec filters, pageSize, the
// inStockOnly toggle and price bounds) read from the current store. Both
// result sets per system, the kept-constrained one only when >=1 component
// is kept.
//
// Skip semantics mirror the page hook WITHOUT the bookkeeping: no wants, no
// summary, a pending summary, or an empty bound contribute no params here —
// recording the honest 'skipped' entries stays the page hook's job (the
// pre-warm is purely additive and must be safe when the user never reaches
// Choose Systems).
export function buildDefaultDuctedFetchParams(
  // Only the slices actually read — a full RootState is assignable, and
  // tests can hand in a store without every app reducer.
  state: DuctedPairContextState & Pick<RootState, 'ui'>
): FetchDuctedCombinationsParams[] {
  // Wants unknown until the structure-load hydration lands: nothing may be
  // warmed off the unhydrated mirror.
  if (!state.structure.recommendationSelectionsLoaded) return [];

  const { ductedFilters, galleryFilters } = state.ui;
  if (
    !isTonnageSeedApplied(
      ductedFilters,
      tonnageSeedKeyOf(resolveStructureTonnageSeed(state))
    )
  ) {
    return [];
  }
  // The same "Sort by" selection the page hook sends, so the warm's page one
  // is byte-identical to the page's first fetch.
  const ductedSort = state.ui.systemSort;
  const params: FetchDuctedCombinationsParams[] = [];

  // Same eligibility as the page hook — MUST widen/narrow together or the
  // pre-warm's requestBodyHash parity with the page's first fetch breaks.
  const eligibleSystems = getChooseSystemsEligibleDuctedSystems(
    state.structure.existingSystems,
    state.structure.recommendationSelectionsBySystem,
    state.structure.wantCompositionPicksBySystem
  );

  // The post-reset fuelSelections come from the SAME composition the reset
  // reducer runs (AHA-6796): seed-sourced → the derived seed; manual-
  // sourced → the current selection pruned to the FULL universe's fuels
  // (resolveContextFuelSelections), so the pre-warm's hash stays
  // byte-identical to the page's first post-reset fetch under preserved
  // manual state too. (Within an already-reset context where the user
  // edits mid-flight the pre-warm can miss the hash — the same accepted
  // imprecision as the chip reset.) The universe is the SAME
  // ductedUniverseCodes derivation the reset hook dispatches from — one
  // shared helper on the same state slices, byte-identical by construction.
  const universeCodes = ductedUniverseCodes(
    eligibleSystems,
    state.structure.recommendationCatalogCodesBySystem,
    state.structure.compatibleResultsBySystem
  );
  const contextFuelSelections = resolveContextFuelSelections(
    ductedFilters.fuelSelections,
    ductedFilters.fuelSelectionsSource,
    ductedSeedFuels(
      eligibleSystems,
      state.structure.recommendationSelectionsBySystem,
      universeCodes
    ),
    availableDuctedFuels(universeCodes)
  );

  // The post-reset filters carry the chassis and orientation compositions
  // the same way (AHA-6796 chassis seed; orientation seed):
  // resetDuctedSystemTypeCodes lands the cabinetChassisSize and orientation
  // enum entries via the SAME resolveContext*Filters compositions, in the
  // same order (seeded upsert, or the manual entry carried verbatim), so
  // the pre-warm's hash stays byte-identical to the page's first post-reset
  // fetch. Same accepted imprecision as the fuel composition within an
  // already-reset, user-edited context.
  const seededFilters = resolveContextOrientationFilters(
    resolveContextChassisFilters(
      ductedFilters.filters,
      ductedFilters.chassisFilterSource,
      ductedSeedChassisSizes(
        eligibleSystems,
        state.structure.wantCompositionPicksBySystem
      )
    ),
    ductedFilters.orientationFilterSource,
    ductedSeedOrientations(eligibleSystems)
  );

  eligibleSystems.forEach((system) => {
    // The wants gateway (sub-build 3): no wants => nothing recommended =>
    // nothing to warm.
    const wantIds =
      state.structure.recommendationSelectionsBySystem[system.uniqueId]
        ?.wantIds ?? [];
    if (wantIds.length === 0) return;

    const summary = state.structure.compatibleResultsBySystem[system.uniqueId];
    const bounded = boundedCodesFor(summary);
    // No summary (bound unresolvable), a first check still in flight, or a
    // zero-result bound: nothing to warm yet — the debounced check cadence
    // re-lands the summary and the caller re-invokes.
    if (bounded === null || summaryPending(summary) || bounded.length === 0) {
      return;
    }

    const keptComponentUniqueIds = ductedKeptComponentIds(system);
    const tonnageRecord =
      state.structure.recommendationSelectionsBySystem[system.uniqueId]
        ?.tonnage ?? null;
    const loads = selectDuctedPairLoads(state, system.uniqueId);
    const coolingOperatingCost = ductedCoolingOperatingCostParamsFromProject({
      locationData: state.project.locationData,
      projectSettings: state.project.projectSettings,
      totalBTUs: loads
    });
    // Same resolver on the same inputs the add path sends into Phase 6, so
    // the design load behind a card's coverage is the one behind the saved
    // system's. With no block load this seed is the ONLY thing standing
    // between the server and an unmeasurable request.
    const tonnageSeed = resolveSystemTonnageSeed({
      system,
      record: tonnageRecord,
      coolingBtu: loads.cooling
    });
    resultSetsFor(keptComponentUniqueIds).forEach((resultSet) => {
      params.push(
        buildDuctedCombinationsFetchParams({
          systemUniqueId: system.uniqueId,
          resultSet,
          keptComponentUniqueIds,
          boundedCodes: bounded,
          chipCodes: [],
          fuelSelections: contextFuelSelections,
          filters: seededFilters,
          inStockOnly: galleryFilters.inStock,
          priceMin: galleryFilters.priceMin,
          priceMax: galleryFilters.priceMax,
          pageNumber: 1,
          pageSize: ductedFilters.pageSize,
          coolingOperatingCost,
          // The relax override rides ui.ductedFilters, so pre-warm/page
          // hash parity holds under it by construction (AHA-6796 T3).
          applySizingBands: ductedFilters.applySizingBands,
          tonnageSeed,
          ductedSort
        })
      );
    });
  });

  return params;
}
